import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { applyAPI, authAPI } from '../api';
import { Link } from 'react-router-dom';

const TIMEZONES = ['EST', 'CST', 'MST', 'PST', 'GMT', 'CET', 'AEST', 'Other'];
const AVAILABILITY = ['Weekdays', 'Weekends', 'Both', 'Evenings only', 'Flexible'];

export default function Apply() {
  const [user, setUser]           = useState(null);
  const [status, setStatus]       = useState(null); // 'pending'|'approved'|'denied'|null
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm]           = useState({
    in_game_name: '', age: '', timezone: '', experience: '', why_join: '', availability: '',
  });
  const token = localStorage.getItem('bce_token');

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    Promise.all([authAPI.getUserProfile(), applyAPI.check()])
      .then(([userRes, checkRes]) => {
        setUser(userRes.data);
        if (checkRes.data.applied) setStatus(checkRes.data.status);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.in_game_name || !form.age || !form.timezone || !form.experience || !form.why_join || !form.availability) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      await applyAPI.submit({ ...form, age: parseInt(form.age) });
      setStatus('pending');
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="bce-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="bce-loading"><div className="bce-spinner" /> Loading...</div>
    </div>
  );

  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)', paddingBottom: '5rem' }}>
      <div className="bce-container-sm" style={{ paddingTop: '4rem' }}>

        <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
          <div style={{ width: 30, height: 2, background: 'var(--bce-gold)' }} />
          <span className="bce-label">Join the Team</span>
        </motion.div>

        <div style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
          <motion.h1 className="bce-display"
            initial={{ y: '100%' }} animate={{ y: 0 }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontSize: 'clamp(3rem, 8vw, 6.5rem)', color: 'var(--bce-white)' }}
          >
            APPLY NOW
          </motion.h1>
        </div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          style={{ color: 'var(--bce-text)', lineHeight: 1.8, marginBottom: '3rem', fontSize: 15 }}>
          Want to be part of the Big Country Energy team? Fill out the form below and we'll review your application.
        </motion.p>

        {/* Not logged in */}
        {!token && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{
              background: 'var(--bce-gold-dim)',
              border: '1px solid var(--bce-gold-border)',
              padding: '2rem',
              textAlign: 'center',
            }}>
            <p style={{ color: 'var(--bce-text)', marginBottom: '1.25rem' }}>
              You must be logged in with Discord to apply.
            </p>
            <button className="bce-btn bce-btn-gold" onClick={async () => {
              const { data } = await authAPI.getDiscordAuthUrl();
              window.location.href = data.url;
            }}>
              Login with Discord
            </button>
          </motion.div>
        )}

        {/* Already applied */}
        {token && status && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'var(--bce-bg-card)',
              border: '1px solid var(--bce-border)',
              borderTop: `3px solid ${status === 'approved' ? 'var(--bce-success)' : status === 'denied' ? 'var(--bce-danger)' : 'var(--bce-gold)'}`,
              padding: '2.5rem',
              textAlign: 'center',
            }}>
            {status === 'pending'  && <Clock size={40} color="var(--bce-gold)" style={{ margin: '0 auto 1rem' }} />}
            {status === 'approved' && <CheckCircle size={40} color="var(--bce-success)" style={{ margin: '0 auto 1rem' }} />}
            {status === 'denied'   && <XCircle size={40} color="var(--bce-danger)" style={{ margin: '0 auto 1rem' }} />}
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
              {status === 'pending'  && 'APPLICATION PENDING'}
              {status === 'approved' && 'APPLICATION APPROVED'}
              {status === 'denied'   && 'APPLICATION DENIED'}
            </h2>
            <p style={{ color: 'var(--bce-text)', fontSize: 14 }}>
              {status === 'pending'  && 'Your application is under review. We\'ll reach out on Discord soon.'}
              {status === 'approved' && 'Welcome to Big Country Energy! Check your Discord for next steps.'}
              {status === 'denied'   && 'Unfortunately your application was not accepted at this time.'}
            </p>
          </motion.div>
        )}

        {/* Form */}
        {token && !status && (
          <motion.form onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div className="bce-field">
                <label>In-Game Name *</label>
                <input className="bce-input" value={form.in_game_name} onChange={e => set('in_game_name', e.target.value)} />
              </div>
              <div className="bce-field">
                <label>Age *</label>
                <input className="bce-input" type="number" min="16" max="80" value={form.age} onChange={e => set('age', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div className="bce-field">
                <label>Timezone *</label>
                <select className="bce-select" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                  <option value="">Select timezone</option>
                  {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="bce-field">
                <label>Availability *</label>
                <select className="bce-select" value={form.availability} onChange={e => set('availability', e.target.value)}>
                  <option value="">Select availability</option>
                  {AVAILABILITY.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            <div className="bce-field">
              <label>Previous Experience *</label>
              <textarea className="bce-textarea" rows={4}
                value={form.experience}
                onChange={e => set('experience', e.target.value)}
                style={{ minHeight: 110 }}
              />
            </div>

            <div className="bce-field">
              <label>Why do you want to join Big Country Energy? *</label>
              <textarea className="bce-textarea" rows={5}
                value={form.why_join}
                onChange={e => set('why_join', e.target.value)}
                style={{ minHeight: 130 }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingTop: '0.5rem' }}>
              <button type="submit" className="bce-btn bce-btn-gold" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
              <span style={{ fontSize: 12, color: 'var(--bce-text-faint)' }}>
                Logged in as <strong style={{ color: 'var(--bce-text)' }}>{user?.username}</strong>
              </span>
            </div>
          </motion.form>
        )}
      </div>
    </div>
  );
}
